import type { ReactElement } from 'react';
import clsx from 'clsx';
import { useState } from 'react';
import {
  Button,
  Select,
  SelectValue,
  ListBox,
  ListBoxItem,
  Popover,
  Label,
  Text,
  Key,
} from 'react-aria-components';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { languages, useLanguage } from '@provider-directory/hooks/useLanguage';
import { ChevronDownIcon } from '../icons/chevron-down';
import { GlobeAlternativeIcon } from '../icons/globe-alternative';

export const HeaderLanguageSelector = (): ReactElement | null => {
  const { language, setLocale } = useLanguage();
  const analytics = useAnalytics();
  const { featureFlags } = useFeatureFlags();
  const [isOpen, setOpen] = useState(false);
  const setSelected = (selected: Key | null) => {
    const languageSelected =
      languages.find((lang) => lang.value === selected) ?? languages[0];
    setLocale(languageSelected);
  };

  if (languages.length <= 1) {
    return null;
  }

  if (!featureFlags.isLocalizationEnabled) {
    return (
      <li className={'relative inline-block align-bottom'}>
        <div
          className={
            'mf-link mf-link--site-montefiore mf-link--light mf-link--base mf-link--direction-backwards mf-link--animation-reverse mf-top-bar-menu__link'
          }
          data-testid={'mmc-language-selector-static'}
        >
          <span className={'mf-link__icon mf-link__icon--leading'}>
            <span
              className={'mf-icon mf-icon--small mf-icon--white'}
              data-analytics-click-text={'globe-alternative'}
              data-analytics-link-type={'icon'}
            >
              <GlobeAlternativeIcon
                className={'mf-icon__svg'}
                aria-hidden={'true'}
                focusable={'false'}
              />
            </span>
          </span>
          <span className={'mf-link__text'}>
            <span className={'mf-link__text__inner'}>English</span>
          </span>
        </div>
      </li>
    );
  }

  const mfListBoxStyles = {
    '--list-vertical-offset': '8px',
    '--list-horizontal-offset': '0px',
    maxHeight: '370px',
  };
  return (
    <li className={'relative inline-block align-bottom'}>
      <Select
        className={'mf-dropdown mf-dropdown--base'}
        data-analytics-event={'e_componentClick'}
        data-analytics-component-type={'dropdown'}
        data-analytics-stop-traversal={''}
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
        selectedKey={language.value}
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
        onSelectionChange={setSelected}
        isOpen={isOpen}
        onOpenChange={(value: boolean) => {
          analytics.componentClickEvent({
            componentType: 'dropdown',
            clickText: language.label,
          });
          setOpen(value);
        }}
        data-testid={'mmc-language-selector'}
      >
        <Label className={'sr-only'}>Change language</Label>
        <Button
          className={
            'mf-link mf-link--site-montefiore mf-link--light mf-link--base mf-link--direction-backwards mf-link--animation-reverse mf-top-bar-menu__link outline-none'
          }
          aria-haspopup={'true'}
          type={'button'}
          data-analytics-click-text={'English'}
          data-analytics-link-type={'link'}
          aria-expanded={'false'}
        >
          <span className={'mf-link__icon mf-link__icon--leading'}>
            <span
              className={'mf-icon mf-icon--small mf-icon--white'}
              data-analytics-click-text={'globe-alternative'}
              data-analytics-link-type={'icon'}
            >
              <GlobeAlternativeIcon
                className={'mf-icon__svg'}
                aria-hidden={'true'}
                focusable={'false'}
              />
            </span>
          </span>
          <SelectValue>
            <span className={'mf-link__text'}>
              <span className={'mf-link__text__inner'}>{language.label}</span>
            </span>
          </SelectValue>

          <span className={'mf-link__icon mf-link__icon--trailing'}>
            <span
              className={'mf-icon mf-icon--small mf-icon--white'}
              data-analytics-click-text={isOpen ? 'chevron-up' : 'chevron-down'}
              data-analytics-link-type={'icon'}
              data-testid={'location-selector-arrow-down'}
            >
              <ChevronDownIcon
                className={clsx('mf-icon__svg', {
                  'rotate-180': isOpen,
                })}
                aria-hidden={'true'}
                focusable={'false'}
              />
            </span>
          </span>
        </Button>

        <Popover offset={0} crossOffset={137}>
          <ListBox
            className={
              'mf-dropdown__list mf-dropdown__list--desktop-right mf-dropdown__list--mobile-center mf-dropdown__list--checked w-[280px]'
            }
            style={mfListBoxStyles}
          >
            {languages.map((option) => (
              <ListBoxItem
                data-testid={`language-option-${option.value}`}
                className={({ isSelected }) =>
                  isSelected
                    ? 'mf-dropdown__item font-bold outline-none'
                    : 'mf-dropdown__item outline-none'
                }
                data-language={option.label}
                data-analytics-link-type={'link'}
                data-analytics-click-text={option.value}
                id={option.value}
                key={option.value}
                textValue={option.label}
              >
                {({ isSelected }) => (
                  <span
                    className={'mf-dropdown__link'}
                    data-language={option.value}
                    data-analytics-link-type={'link'}
                    data-analytics-click-text={option.label}
                  >
                    <span className={'mf-dropdown__link__text'}>
                      <Text
                        slot={'label'}
                        className={
                          'mf-text-body-3-sans lg:whitespace-normal mf-text-gray-700 truncate'
                        }
                      >
                        {option.label}
                      </Text>
                    </span>
                    {isSelected ? <span className={'fabric-checked'} /> : null}
                  </span>
                )}
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </Select>
    </li>
  );
};
